import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LOG_CONTEXT } from '@rms/core/http';
import { pagedParams } from '@rms/core/pagination';
import { environment } from '@rms/env';
import { PageableParam, Paginated } from '@rms/sdk/models';
import { Filter } from '@rms/ui/filters';
import { Observable } from 'rxjs';

import {
  Partner,
  PartnerCreateRequest,
  PartnerPatchRequest,
  PartnerRole,
  PartnerUserCreateRequest,
  PartnerUserResponse,
  PartnerUserUpdateRequest,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class PartnerApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiEndpoint}/v1/im/partners`;

  public getPartners(pageable: PageableParam, filters: Filter[] = []): Observable<Paginated<Partner>> {
    return this.http.get<Paginated<Partner>>(this.baseUrl, {
      params: pagedParams(pageable, filters),
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.getPartners'),
    });
  }

  public getPartner(partnerId: string): Observable<Partner> {
    return this.http.get<Partner>(`${this.baseUrl}/${partnerId}`, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.getPartner'),
    });
  }

  public getPartnerUsers(
    partnerId: string,
    pageable: PageableParam,
    filters: Filter[] = []
  ): Observable<Paginated<PartnerUserResponse>> {
    return this.http.get<Paginated<PartnerUserResponse>>(`${this.baseUrl}/${partnerId}/users`, {
      params: pagedParams(pageable, filters),
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.getPartnerUsers'),
    });
  }

  public createPartnerUser(partnerId: string, request: PartnerUserCreateRequest): Observable<PartnerUserResponse> {
    return this.http.post<PartnerUserResponse>(`${this.baseUrl}/${partnerId}/users`, request, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.createPartnerUser'),
    });
  }

  public updatePartnerUser(
    partnerId: string,
    userId: string,
    request: PartnerUserUpdateRequest
  ): Observable<PartnerUserResponse> {
    return this.http.put<PartnerUserResponse>(`${this.baseUrl}/${partnerId}/users/${userId}`, request, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.updatePartnerUser'),
    });
  }

  public getPartnerRoles(): Observable<PartnerRole[]> {
    return this.http.get<PartnerRole[]>(`${this.baseUrl}/roles`, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.getPartnerRoles'),
    });
  }

  public updatePartnerUserRole(partnerId: string, userId: string, roleIds: string[]): Observable<PartnerUserResponse> {
    return this.http.put<PartnerUserResponse>(
      `${this.baseUrl}/${partnerId}/users/${userId}/roles`,
      { role_ids: roleIds },
      { context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.updatePartnerUserRole') }
    );
  }

  public setPartnerUserPassword(partnerId: string, userId: string, password: string): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/${partnerId}/users/${userId}/credentials`,
      { password },
      { context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.setPartnerUserPassword') }
    );
  }

  public deletePartnerUser(partnerId: string, userId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${partnerId}/users/${userId}`, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.deletePartnerUser'),
    });
  }

  public createPartner(request: PartnerCreateRequest): Observable<Partner> {
    return this.http.post<Partner>(this.baseUrl, request, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.createPartner'),
    });
  }

  public updatePartner(partnerId: string, request: PartnerPatchRequest): Observable<Partner> {
    return this.http.patch<Partner>(`${this.baseUrl}/${partnerId}`, request, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.updatePartner'),
    });
  }

  public deletePartner(partnerId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${partnerId}`, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.deletePartner'),
    });
  }
}
